// motion.js — lightweight motion directives, no external dependencies.
//
// Register in main.js:
//   import { revealDirective, countDirective, tiltDirective } from './motion.js'
//   app.directive('reveal', revealDirective)
//   app.directive('count', countDirective)
//   app.directive('tilt', tiltDirective)

/**
 * v-reveal="{ delay: 150, variant: 'left' }"
 * Fades/slides an element in the first time it enters the viewport.
 * variant: 'up' (default) | 'left' | 'right' | 'scale'
 */
export const revealDirective = {
  mounted(el, binding) {
    const variant = binding.value?.variant
    el.classList.add('rk-reveal')
    if (variant) el.classList.add(`rk-reveal--${variant}`)
    if (binding.value?.delay) el.style.setProperty('--reveal-delay', `${binding.value.delay}ms`)

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add('rk-reveal--visible')
            observer.unobserve(el)
          }
        })
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    )
    observer.observe(el)
  },
}

/**
 * v-count="2500"  (on a <span>, counts up from 0 to 2500 when it scrolls into view)
 * Optional formatting: v-count="{ value: 96, suffix: '%' }"
 */
export const countDirective = {
  mounted(el, binding) {
    const config = typeof binding.value === 'object' ? binding.value : { value: binding.value }
    const target = Number(config.value) || 0
    const prefix = config.prefix || ''
    const suffix = config.suffix || ''
    const duration = config.duration || 1400

    const format = (n) => `${prefix}${Math.round(n).toLocaleString()}${suffix}`
    el.textContent = format(0)

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          observer.unobserve(el)
          const start = performance.now()
          const step = (now) => {
            const progress = Math.min((now - start) / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3) // ease-out cubic
            el.textContent = format(target * eased)
            if (progress < 1) requestAnimationFrame(step)
          }
          requestAnimationFrame(step)
        })
      },
      { threshold: 0.5 }
    )
    observer.observe(el)
  },
}

/**
 * v-tilt (on a card) — subtle pointer-driven 3D tilt. Pairs with the .rk-tilt
 * class in design-tokens.css. Skips itself on touch devices.
 */
export const tiltDirective = {
  mounted(el) {
    if (window.matchMedia('(hover: none)').matches) return
    el.classList.add('rk-tilt')

    const strength = 6 // max degrees of rotation
    const onMove = (e) => {
      const rect = el.getBoundingClientRect()
      const px = (e.clientX - rect.left) / rect.width - 0.5
      const py = (e.clientY - rect.top) / rect.height - 0.5
      el.style.setProperty('--ry', `${px * strength * 2}deg`)
      el.style.setProperty('--rx', `${py * -strength * 2}deg`)
    }
    const onLeave = () => {
      el.style.setProperty('--rx', '0deg')
      el.style.setProperty('--ry', '0deg')
    }

    el.addEventListener('mousemove', onMove)
    el.addEventListener('mouseleave', onLeave)
  },
}